import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { formatNumber } from '../../../../../Utils';
import {
    calcPMT,
    solveRate,
    parseMoney,
    maxResidualForPlazo,
    getResidualSteps,
} from './pmt.js';

const IVA = 0.16;
const ALL_PLAZOS = [24, 36, 48, 60];

function formatMoney(n) {
    if (!Number.isFinite(n) || n <= 0) return '—';
    return `$${formatNumber(n.toFixed(2))}`;
}

function pickNeighborPlazos(masterPlazo, availablePlazos) {
    const available = ALL_PLAZOS.filter(p => availablePlazos.includes(p));
    if (available.length <= 3) return available;
    const idx = available.indexOf(masterPlazo);
    if (idx < 0) return available.slice(0, 3);
    if (idx === 0) return available.slice(0, 3);
    if (idx === available.length - 1) return available.slice(-3);
    return available.slice(idx - 1, idx + 2);
}

function ResidualMatrixTable({ state }) {
    const [mode, setMode] = useState('base');

    const { plazos, rows, currentPlazo, hasCapped } = useMemo(() => {
        const pdfPlazo = (state?.pdfPlazo || []);
        const V = (Number(state?.rodac?.amount) || 0) + (Number(state?.rodac?.accesorios) || 0);
        const currentResidualPct = Number(state?.residualValue?.percentage) || 5;
        const selectedPlazo = pdfPlazo.find(p => p.selected)?.plazo
            ?? pdfPlazo.find(p => !p.ignore)?.plazo;

        const perPlazo = pdfPlazo.map(p => {
            const rentaBase = parseMoney(p?.section3?.['Renta']?.[0]);
            const gastosAdm = parseMoney(p?.section3?.['Gastos Administrativos']?.[0]);
            const rentaEfectiva = rentaBase + gastosAdm;

            const totalSinIva = parseMoney(p?.section3?.['Total Pago Mensual (sin IVA)']?.[0]);
            const services = Math.max(0, totalSinIva - rentaEfectiva);

            const gastoInicial = parseMoney(p?.section2?.['Gasto Inicial Arrendamiento']?.[0]);
            const E = gastoInicial || 0;
            const amtFinSinIva = Math.max(0, (V - E) / (1 + IVA));

            // V ya incluye IVA → residual c/IVA = V * pct%; sin IVA = / 1.16
            const effectivePct = Math.min(currentResidualPct, maxResidualForPlazo(p.plazo) || currentResidualPct);
            const residualConIva = V * (effectivePct / 100);
            const residualSinIva = residualConIva / (1 + IVA);

            let rate = 0;
            if (rentaEfectiva > 0 && amtFinSinIva > 0) {
                rate = solveRate(rentaEfectiva, p.plazo, amtFinSinIva, residualSinIva);
            }

            return { plazo: p.plazo, rate, amtFinSinIva, services };
        });

        const plazosOrdered = perPlazo.sort((a, b) => a.plazo - b.plazo);
        const availablePlazos = plazosOrdered.map(p => p.plazo);
        const visiblePlazos = pickNeighborPlazos(selectedPlazo, availablePlazos);
        const visibleMeta = visiblePlazos
            .map(p => plazosOrdered.find(x => x.plazo === p))
            .filter(Boolean);

        const masterCap = maxResidualForPlazo(selectedPlazo) || 50;
        const matrixMax = Math.min(50, masterCap + 5);
        const steps = getResidualSteps(5, matrixMax);
        let matrixHasCapped = false;

        const rows = steps.map(pct => {
            const cells = visibleMeta.map(({ plazo, rate, amtFinSinIva, services }) => {
                const limit = maxResidualForPlazo(plazo) || pct;
                const effectivePct = Math.min(pct, limit);
                const capped = pct > limit;
                if (capped) matrixHasCapped = true;

                const residualConIva = V * (effectivePct / 100);
                const residualSinIva = residualConIva / (1 + IVA);

                if (rate <= 0 || amtFinSinIva <= 0) {
                    return { value: null, disabled: true, capped, effectivePct };
                }
                const rentaEfectiva = calcPMT(rate, plazo, amtFinSinIva, residualSinIva);
                const value = mode === 'base'
                    ? rentaEfectiva * (1 + IVA)
                    : (rentaEfectiva + services) * (1 + IVA);
                return { value, disabled: false, capped, effectivePct };
            });

            const rowResidualConIva = V * (pct / 100);
            const rowResidualSinIva = rowResidualConIva / (1 + IVA);
            return {
                pct,
                residualSinIva: rowResidualSinIva,
                residualValue: rowResidualConIva,
                cells,
                isCurrent: pct === currentResidualPct,
            };
        });

        return {
            plazos: visiblePlazos,
            rows,
            currentPlazo: selectedPlazo,
            hasCapped: matrixHasCapped,
        };
    }, [state, mode]);

    if (!plazos.length) return null;

    return (
        <div className="ResidualMatrix">
            <div className="MatrixHeader">
                <div className="MatrixTitle">
                    <span className="Dot" />
                    <h3>Matriz de Renta Mensual</h3>
                    <span className="Subtitle">por residual y plazo</span>
                </div>
                <div className="ModeToggle" role="tablist">
                    <button
                        role="tab"
                        aria-selected={mode === 'base'}
                        className={mode === 'base' ? 'active' : ''}
                        onClick={() => setMode('base')}
                    >
                        Renta base
                    </button>
                    <button
                        role="tab"
                        aria-selected={mode === 'total'}
                        className={mode === 'total' ? 'active' : ''}
                        onClick={() => setMode('total')}
                    >
                        Con servicios
                    </button>
                    <motion.div
                        className="ToggleIndicator"
                        layout
                        initial={false}
                        animate={{ x: mode === 'base' ? 0 : '100%' }}
                        transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                    />
                </div>
            </div>

            <div className="MatrixScroll">
                <table className="MatrixTable">
                    <thead>
                        <tr>
                            <th className="col-head">Residual</th>
                            <th className="col-head money">Residual $ c/IVA</th>
                            {plazos.map(p => (
                                <th
                                    key={p}
                                    className={`col-head ${p === currentPlazo ? 'current-col' : ''}`}
                                >
                                    {p}m
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        <AnimatePresence mode="wait">
                            <motion.tr
                                key="placeholder"
                                style={{ display: 'none' }}
                            />
                        </AnimatePresence>
                        {rows.map((row, rIdx) => (
                            <motion.tr
                                key={`${row.pct}-${mode}`}
                                className={row.isCurrent ? 'current-row' : ''}
                                initial={{ opacity: 0, y: 6 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: rIdx * 0.02, duration: 0.25 }}
                            >
                                <td className="pct-cell">
                                    <span className="pct-badge">{row.pct}%</span>
                                </td>
                                <td className="money residual-cell">
                                    {formatMoney(row.residualValue)}
                                </td>
                                {row.cells.map((cell, cIdx) => (
                                    <td
                                        key={cIdx}
                                        className={`money plazo-cell ${cell.disabled ? 'disabled' : ''} ${cell.capped ? 'capped' : ''} ${plazos[cIdx] === currentPlazo ? 'current-col' : ''}`}
                                        title={cell.capped ? `Ajustado al máximo del plazo: ${cell.effectivePct}%` : undefined}
                                    >
                                        <motion.span
                                            key={`${mode}-${row.pct}-${cIdx}`}
                                            initial={{ opacity: 0, scale: 0.96 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            transition={{ duration: 0.18, delay: cIdx * 0.015 }}
                                        >
                                            {cell.disabled ? '—' : cell.capped ? '' : formatMoney(cell.value)}
                                        </motion.span>
                                    </td>
                                ))}
                            </motion.tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="MatrixFooter">
                <div className="LegendRow">
                    <span className="LegendDot current" /> fila del residual seleccionado
                    <span className="LegendSep">·</span>
                    <span className="LegendDot disabled" /> combinación no disponible
                    {hasCapped && (
                        <>
                            <span className="LegendSep">·</span>
                            <span className="LegendDot capped" /> residual ajustado al máximo del plazo
                        </>
                    )}
                </div>
                <div className="MatrixDisclaimer">
                    * Solo el monto del residual/plazo seleccionado es exacto. Los demás son aproximados y podrían variar al cotizarlos.
                </div>
            </div>
        </div>
    );
}

export { ResidualMatrixTable };
